import { useEffect, useState } from 'react';
import { useAtomValue as useStateSegmentValue } from 'jotai';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { RiFileForbidLine } from '@remixicon/react';
import ReactMarkdown from 'react-markdown';
import styles from './BrandDetailsTab.styles.module.css';
import BrandDetails from '../../../atoms/BrandDetails';
import {
  AdditionalBrandDetail,
  BrandDetails as BrandDetailsType,
  DisclaimerInfo,
  knowledgeCenterStateSegment,
  PrescribingInfo,
  SafetyInfo,
  SupportingInfo,
  SupportingInformationType,
} from '../../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { translate } from '../../../../localization';
import Loading from '../../Loading';
import getFormattedCustomerType from '../../../../utils/getFormattedCustomerType';
import { fetchBrandDetails } from '../../../../store/markettingContentGeneration/actions/majorProcessActions/fetchBrandDetails';
import useBrandMessageState from '../../../../hooks/brandInfoPage/useBrandMessageState';
import BrandsMessage from '../BrandsMessage';
import InfoLogo from '../InfoLogo';
import Button from '../../../atoms/Button';
import Retry from '../Retry';
import components from '../../Markdown/components';
import { INCLUDES_PARAMS, PATIENT } from '../../../../constants/common';
import hasValidValues from '../../../../utils/hasValidValues';

export default function BrandDetailsTab() {
  const [retryCount, setRetryCount] = useState(0);

  const { brandDetails, apiPayloadUsed } = useStateSegmentValue(
    knowledgeCenterStateSegment
  );

  const { isLoading, errorMessage } = brandDetails || {};

  const brandDetailsData = get(brandDetails, 'data', {} as BrandDetailsType);
  const name = get(brandDetailsData, 'name', '');
  const description = get(brandDetailsData, 'description', '');
  const additionalBrandDetails = get(
    brandDetailsData,
    'additionalBrandDetails',
    [] as AdditionalBrandDetail[]
  );
  const supportingInfo = get(
    brandDetailsData,
    'supportingInfo',
    [] as SupportingInfo[]
  );

  const {
    state: brandMessageConfig,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
  } = useBrandMessageState();

  const handleTabLevelRetry = async () => {
    const brandsApiPayload = {
      ...apiPayloadUsed,
      includes: INCLUDES_PARAMS,
    };
    const success = await fetchBrandDetails(brandsApiPayload);

    if (success) {
      setRetryCount(0);
    } else {
      setRetryCount(prev => prev + 1);
    }
  };

  useEffect(() => {
    if (!isLoading && errorMessage?.length === 0 && isEmpty(brandDetailsData)) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
      });
      // if there is no data for the tab, then this useEffect will handle it
    }
  }, [isLoading, brandDetailsData]);

  useEffect(() => {
    if (errorMessage?.length > 0) {
      updateScenarioTabLevelError(
        <InfoLogo />,
        <Button
          type='button'
          size='m'
          variant='outline'
          colorScheme='#0000C9'
          onClick={handleTabLevelRetry}
        >
          <Retry />
        </Button>
      );
    }
  }, [errorMessage?.length]);

  useEffect(() => {
    if (retryCount > 3) {
      updateScenarioTabLevelApiDown(<InfoLogo />);
    }
  }, [retryCount]);

  if (isLoading) {
    return (
      <div className={styles['brands-details__loading']}>
        <Loading />
      </div>
    );
  }

  if (!isLoading && (isEmpty(brandDetailsData) || errorMessage?.length > 0)) {
    const { cta, logo, message, title } = brandMessageConfig || {};

    return (
      <div className={styles['brands-details__loading']}>
        <BrandsMessage logo={logo} title={title} message={message} cta={cta} />
      </div>
    );
  }

  const renderBrandDescriptionByIndication = (
    additionalBrandDetailsArray: AdditionalBrandDetail[]
  ) => {
    return (
      <div
        className={
          styles['brand-details_tab__additional-brand-details-container']
        }
      >
        {additionalBrandDetailsArray?.map(item => {
          const indicationName = get(item, 'indicationName', '');
          const indicationBrandDescription = get(
            item,
            'indicationBrandDescription',
            ''
          );

          return (
            item &&
            Object.keys(item)?.length > 0 &&
            hasValidValues(item) && (
              <div key={indicationName}>
                {indicationName && indicationName != null && (
                  <div
                    className={
                      styles['additional-brand-details__indication-name']
                    }
                  >
                    {indicationName}
                  </div>
                )}

                {indicationBrandDescription &&
                  indicationBrandDescription != null && (
                    <ReactMarkdown
                      components={components}
                      className={
                        styles[
                          'additional-brand-details__indication-brand-description'
                        ]
                      }
                    >
                      {indicationBrandDescription}
                    </ReactMarkdown>
                  )}
              </div>
            )
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles['brand-details_tab__container']}>
      <div className={styles['brand-details_tab__brands_information_content']}>
        <BrandDetails
          title={name}
          text={description}
          classes={styles['brand-details_tab__brands_information_container']}
        />

        <BrandDetails
          title={`${translate('brand_description_by_indication')}${
            additionalBrandDetails?.length > 0
              ? ` (${additionalBrandDetails?.length})`
              : ''
          }`}
          text={
            additionalBrandDetails && additionalBrandDetails?.length > 0
              ? renderBrandDescriptionByIndication(additionalBrandDetails)
              : ''
          }
          classes={styles['brand-details_tab__brands_information_container']}
          initialHeight={100}
        />
      </div>

      {supportingInfo?.map(info => {
        const audienceName = get(info, 'audienceName', 'Customer Type');
        const supportingInfoData = get(
          info,
          'supportingInfo',
          {} as SupportingInformationType
        );
        const prescribingInfo = get(
          supportingInfoData,
          'prescribingInfo',
          [] as PrescribingInfo[]
        );
        const disclaimerInfo = get(
          supportingInfoData,
          'disclaimerInfo',
          [] as DisclaimerInfo[]
        );
        const safetyInfo = get(
          supportingInfoData,
          'safetyInfo',
          {} as SafetyInfo
        );
        const safetyInfoText = get(safetyInfo, 'text', '');

        return (
          <div
            className={styles['brand-details_tab__brands_information_content']}
            key={audienceName}
          >
            {disclaimerInfo?.length > 0 && (
              <BrandDetails
                title={translate('supporting_information_disclaimers_title')}
                text=''
                isLink
                piLinks={disclaimerInfo}
                isForPatient={
                  audienceName?.toLowerCase() === PATIENT.toLowerCase()
                }
                classes={
                  styles['brand-details_tab__brands_information_container']
                }
              />
            )}

            <BrandDetails
              title={`${translate(
                'full_prescribing_info_for'
              )} ${getFormattedCustomerType(audienceName)}`}
              text=''
              isLink
              piLinks={prescribingInfo}
              isForPatient={
                audienceName?.toLowerCase() === PATIENT.toLowerCase()
              }
              classes={
                styles['brand-details_tab__brands_information_container']
              }
            />

            <BrandDetails
              title={translate('important_safety_information')}
              text={safetyInfoText}
              classes={
                styles['brand-details_tab__brands_information_container']
              }
            />
          </div>
        );
      })}
    </div>
  );
}
